/**
 * useForwardMessage — Hook para encaminhar mensagens do chat
 *
 * Responsabilidades:
 *  - Listar CONTATOS do tenant (RPC forward_message_list_targets v2.0.0)
 *  - Criar job de encaminhamento por contato + inbox de origem
 *    (RPC forward_message_create_job v2.0.0) — reaproveita
 *    get_or_create_conversation no backend para criar conversa
 *    na inbox de origem quando ainda não existir.
 *  - Assinar realtime em chat_forward_jobs para acompanhar progresso
 *
 * CHANGELOG:
 *   2026-06-13 v2.0.0 - Operação por contato + sourceInboxId. Para WAHA/Z-API
 *     o sistema cria conversa nova na inbox de origem se necessário; para
 *     Meta WABA continua exigindo janela 24h aberta.
 *   2026-06-13 v1.0.0 - Versão inicial. Apenas provider Meta WABA.
 */

import { useCallback, useEffect, useState } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { createLogger } from '@/lib/logger';

const log = createLogger('useForwardMessage');

export interface ForwardTarget {
  contact_id: string;
  contact_name: string | null;
  contact_phone: string | null;
  contact_avatar_url: string | null;
  /** ID da conversa existente na inbox de origem (null se ainda não houver). */
  conversation_id: string | null;
  last_inbound_at: string | null;
  window_24h_open: boolean;
  /** TRUE se o sistema permite encaminhar agora (WAHA/Z-API sempre; Meta exige janela). */
  can_forward: boolean;
}

export interface ForwardJob {
  id: string;
  status: 'queued' | 'processing' | 'partial' | 'done' | 'failed';
  total: number;
  succeeded: number;
  failed: number;
  error_summary: Array<Record<string, unknown>>;
}

export interface BlockedTarget {
  contact_id?: string;
  conversation_id?: string;
  reason: 'window_24h_closed' | 'provider_not_supported';
  last_inbound_at?: string | null;
  provider_slug?: string;
}

// Limites — alinhados ao backend (forward_message_create_job)
export const FORWARD_MAX_MESSAGES = 10;
export const FORWARD_MAX_TARGETS = 5;

export function useForwardMessage() {
  const [targets, setTargets] = useState<ForwardTarget[]>([]);
  const [loadingTargets, setLoadingTargets] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeJob, setActiveJob] = useState<ForwardJob | null>(null);

  // Busca contatos do tenant para encaminhar via inbox de origem
  const loadTargets = useCallback(async (sourceInboxId: string, search?: string) => {
    if (!sourceInboxId) {
      setTargets([]);
      return;
    }
    setLoadingTargets(true);
    try {
      const { data, error } = await supabase.rpc('forward_message_list_targets', {
        p_source_inbox_id: sourceInboxId,
        p_search: search || null,
        p_limit: 50,
      });
      if (error) throw error;
      setTargets((data || []) as ForwardTarget[]);
    } catch (err) {
      log.error('Falha ao carregar destinos', err);
      toast.error('Não foi possível carregar destinos');
      setTargets([]);
    } finally {
      setLoadingTargets(false);
    }
  }, []);

  // Cria o job e enfileira as tarefas
  const forward = useCallback(
    async (sourceMessageIds: string[], targetContactIds: string[], sourceInboxId: string) => {
      if (sourceMessageIds.length === 0 || targetContactIds.length === 0) {
        toast.error('Selecione ao menos 1 mensagem e 1 destino');
        return null;
      }
      if (!sourceInboxId) {
        toast.error('Inbox de origem não identificada');
        return null;
      }
      if (sourceMessageIds.length > FORWARD_MAX_MESSAGES) {
        toast.error(`Máximo de ${FORWARD_MAX_MESSAGES} mensagens por encaminhamento`);
        return null;
      }
      if (targetContactIds.length > FORWARD_MAX_TARGETS) {
        toast.error(`Máximo de ${FORWARD_MAX_TARGETS} destinos por encaminhamento`);
        return null;
      }

      setSubmitting(true);
      try {
        const { data, error } = await supabase.rpc('forward_message_create_job', {
          p_source_message_ids: sourceMessageIds,
          p_target_contact_ids: targetContactIds,
          p_source_inbox_id: sourceInboxId,
        });
        if (error) throw error;

        const row = Array.isArray(data) ? data[0] : data;
        const jobId = row?.job_id as string | undefined;
        const total = row?.total as number | undefined;
        const blocked = (row?.blocked_targets || []) as unknown as BlockedTarget[];

        if (!jobId) throw new Error('Job não retornado pela RPC');

        setActiveJob({
          id: jobId,
          status: 'queued',
          total: total || 0,
          succeeded: 0,
          failed: 0,
          error_summary: [],
        });

        if (blocked.length > 0) {
          toast.warning(
            `${blocked.length} destino(s) ignorado(s): janela 24h fechada`,
          );
        }
        toast.success(`Encaminhando ${total} mensagem(ns)...`);
        return jobId;
      } catch (err) {
        const msg = err instanceof Error ? err.message : String(err);
        log.error('Falha ao criar job de encaminhamento', err);
        toast.error(`Falha ao encaminhar: ${msg}`);
        return null;
      } finally {
        setSubmitting(false);
      }
    },
    [],
  );

  // Realtime: acompanha progresso do job ativo
  useEffect(() => {
    if (!activeJob?.id) return;

    const channel = supabase
      .channel(`chat_forward_jobs:${activeJob.id}`)
      .on(
        'postgres_changes',
        { event: 'UPDATE', schema: 'public', table: 'chat_forward_jobs', filter: `id=eq.${activeJob.id}` },
        (payload) => {
          const row = payload.new as Record<string, unknown>;
          setActiveJob((prev) =>
            prev
              ? {
                  ...prev,
                  status: row.status as ForwardJob['status'],
                  succeeded: (row.succeeded as number) ?? prev.succeeded,
                  failed: (row.failed as number) ?? prev.failed,
                  error_summary: (row.error_summary as ForwardJob['error_summary']) ?? prev.error_summary,
                }
              : prev,
          );

          // Toast final
          const status = row.status as string;
          if (status === 'done') toast.success('Encaminhamento concluído');
          else if (status === 'partial')
            toast.warning(`Concluído com falhas (${row.succeeded}/${(row.succeeded as number) + (row.failed as number)})`);
          else if (status === 'failed') toast.error('Encaminhamento falhou');
        },
      )
      .subscribe();

    return () => {
      supabase.removeChannel(channel);
    };
  }, [activeJob?.id]);

  const clearActiveJob = useCallback(() => setActiveJob(null), []);

  return {
    targets,
    loadingTargets,
    loadTargets,
    forward,
    submitting,
    activeJob,
    clearActiveJob,
  };
}
